import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { toast } from 'sonner';
import { z } from 'zod';
import Honeybadger from '@honeybadger-io/js';
import { zodResolver } from '@hookform/resolvers/zod';

import { deserializer } from '../../../../shared/utils/deserializer';
import { SUCCESS_MESSAGES } from '../constants';
import {
  createAppointment as apiCreateAppointment,
  updateAppointment as apiUpdateAppointment,
} from '../services/appointmentService';

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '';
  return dateTimeString.slice(0, 16); // Remove seconds and the timezone.
};

const formSchema = z.object({
  description: z.string().optional(),
  endAt: z.string().min(1, 'End time is required.'),
  startAt: z.string().min(1, 'Start time is required.'),
  title: z.string().min(1, 'Title is required.'),
  userId: z.string().min(1, 'User is required.'),
});

const useAppointmentForm = ({ activeAppointment, onClose, onCreate, onUpdate }) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = {
    description: activeAppointment?.description ?? '',
    endAt: formatDateTime(activeAppointment?.endAt),
    startAt: formatDateTime(activeAppointment?.startAt),
    title: activeAppointment?.title ?? '',
    userId: activeAppointment?.users[0]?.id ?? '',
  };

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  // For repopulating :defaultValues when :activeAppointment changes.
  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAppointment]);

  const createAppointment = async (appointmentData) => {
    const serializedAppointment = await apiCreateAppointment(appointmentData);
    return deserializer.deserialize(serializedAppointment);
  };

  const updateAppointment = async (appointmentData, appointmentId) => {
    const serializedAppointment = await apiUpdateAppointment({
      appointmentData,
      appointmentId,
    });

    return deserializer.deserialize(serializedAppointment);
  };

  const handleSuccess = (appointment) => {
    if (activeAppointment) {
      onUpdate(appointment);
      toast.success(SUCCESS_MESSAGES.appointment.update);
    } else {
      onCreate(appointment);
      toast.success(SUCCESS_MESSAGES.appointment.create);
    }

    onClose();
    form.reset();
  };

  const handleSubmit = async (formData) => {
    try {
      setErrorMessage('');
      setIsSubmitting(true);

      const appointment = activeAppointment
        ? await updateAppointment(formData, activeAppointment.id)
        : await createAppointment(formData);

      handleSuccess(appointment);
    } catch (error) {
      setErrorMessage(error.message);
      Honeybadger.notify(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open) => {
    if (!open) {
      onClose();
      setErrorMessage('');
      form.reset();
    }
  };

  return {
    errorMessage,
    form,
    handleOpenChange,
    handleSubmit,
    isSubmitting,
  };
};

export default useAppointmentForm;
